import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { parseISO } from 'date-fns';
import { Subject, merge, takeUntil, tap } from 'rxjs';
import { QQQuestionnaireViewModel } from 'src/app/models/qq-questionnaire.view-model';
import { QuoteModel } from 'src/app/models/quote.model';
import { VerifyOtpResponse } from 'src/app/models/verify-otp.response';
import { ApiService } from 'src/app/services/api.service';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { Dictionary } from 'src/app/util/to-dictionary';

@Component({
  selector: 'app-verify-quote',
  templateUrl: './verify-quote.component.html',
  styleUrls: ['./verify-quote.component.scss'],
})
export class VerifyQuoteComponent implements OnInit, OnDestroy {
  @Input() key: string;
  @Output() loaded = new EventEmitter<QuoteModel>();
  @Output() back = new EventEmitter();
  @ViewChild(NgForm) form: NgForm;
  @ViewChild('quoteList', { static: false }) quoteList: ElementRef;

  questionnaire: QQQuestionnaireViewModel;
  errorKey: string;
  mode: 'form' | 'otp' | 'list-quotes';
  otpKey: string;
  loadNextQuotes: () => Promise<any>;
  quotes: QuoteInfo[];
  loading = false;
  loadingQuotes = false;

  dispose$ = new Subject();

  get page() {
    return this.questionnaire?.currentPage;
  }

  constructor(
    private api: ApiService,
    private questionnaireService: QuestionnaireService) {
  }

  async ngOnInit() {
    this.mode = 'form';
    this.questionnaire = await this.questionnaireService.getQuestionnaireViewModel('VerifyQuote', false);

    merge(...this.questionnaire.pages.map(x => x.onQuestionAnswered)).pipe(
      takeUntil(this.dispose$),
      tap(x => this.errorKey = null)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.dispose$.next({});
  }

  async saveOtp(rsp: VerifyOtpResponse) {
    this.loading = true;
    try {
      this.loadNextQuotes = null;
      let p = 0
      const { Email, DateOfBirth, LastName } = this.questionnaire.answersObject;

      this.quotes = [];
      this.loadNextQuotes = async () => {
        const quotesRsp = await this.api.listQuotes(rsp.code, Email, LastName, DateOfBirth, p, 8);
        this.quotes.push(...quotesRsp.quotes.map(x => new QuoteInfo(x)));
        p += 1;
        if (!quotesRsp.hasMore)
          this.loadNextQuotes = null;
      };
      await this.loadNextQuotes();
      this.mode = 'list-quotes';
    } finally {
      this.loading = false;
    }
    //this.loaded.next(quote);
  }

  async next() {
    this.loading = true;
    try {
      if (!this.key) {
        await this.sendOtp();
      }
      else {
        const quote = await this.api.getQuote(this.key, this.questionnaire.answersObject.DateOfBirth, this.questionnaire.answersObject.LastName);
        this.loaded.next(quote);
      }
    } catch (err: any) {
      this.errorKey = "FullQuote.NotFoundError";
      //this.error = 'Could not find a matching quote, please check your details or start a new quote';
    } finally {
      this.loading = false;
    }
  }

  async sendOtp() {
    this.loading = true;
    try {
      const { Email, DateOfBirth, LastName } = this.questionnaire.answersObject;
      const rsp = await this.api.sendQuoteOtp(Email, DateOfBirth, LastName);
      this.otpKey = rsp.key;
      this.mode = 'otp';
    } finally {
      this.loading = false;
    }
  }

  async loadMoreQuotes() {
    console.log('loadMoreQuotes %o', this.loadingQuotes);
    if (this.loadingQuotes)
      return;

    this.loadingQuotes = true;
    try {
      if (this.loadNextQuotes)
        await this.loadNextQuotes();
    } finally {
      this.loadingQuotes = false;
    }
  }
}

export class QuoteInfo {
  date: Date;
  sumAssured: number;
  quoteNo: string;
  term: number;
  policyType: string;
  premium: number;
  coverBasis: string;
  answers: Dictionary<string>;

  constructor(public quote: QuoteModel) {
    this.answers = quote.answers.toDictionary(x => x.key, x => x.value);
    this.date = parseISO(quote.quoteDate);
    this.sumAssured = parseFloat(this.answer('ProtectionLevel_1'));
    this.quoteNo = quote.result.quoteNo;
    this.premium = quote.result.premium;
    this.term = parseInt(this.answer('PolicyTerm_1'));
    this.policyType = this.answer('PolicyType_2') || this.answer('PolicyType_1');
    this.coverBasis = this.answer('CoverBasis_1');
  }

  answer(key: string) {
    return this.answers[key];
  }

  describe() {
    let prefix = this.answer('CoverBasis_1') == 'DECREASING' ? 'Decreasing ' : 'Level ';
    prefix += ' ' + this.answer('TypeOfPolicy_1') == 'DUAL' ? "Dual " : "";

    return prefix + "Term Life Cover";
  }
}
